import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import PaginationComponent from "../../../components/Pagination/Pagination";
import TableBackground from "../../../components/TableBackground";
import EmptyState from "../../../components/EmptyState";
import { getAgentReferrals } from "../../../redux/Agents/agentsAction";
import { debounce } from "lodash";
import SearchComponent from "../../../components/Search";

const ReferralsTable = () => {
  const [status, setStatus] = useState("all");
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  const { agentReferralData, agentReferralTotal, agentReferralLoading } =
    useSelector((state) => state.agents);

  const onPageChange = (val) => {
    const { page } = val;
    setCurrentPage(page);

    dispatch(
      getAgentReferrals({
        id: id,
        page: currentPage,
        search: page,
        status: status,
      })
    );
  };

  const handleSearch = useMemo(
    () =>
      debounce((val) => {
        //  api to dispatch and make the actions for search
        dispatch(
          getAgentReferrals({
            id: id,
            page: currentPage,
            search: val,
            status: status,
          })
        );
      }, 750),
    [dispatch, status, currentPage, id]
  );

  const handleChange = useCallback(
    (e) => {
      let val = e.target.value;
      setSearchValue(val);
      handleSearch(val);
    },
    [handleSearch]
  );

  const handleStatusChange = (tabStatus) => {
    setStatus(tabStatus);
    dispatch(
      getAgentReferrals({
        id: id,
        page: currentPage,
        search: searchValue,
        status: status,
      })
    );
  };

  useEffect(() => {
    dispatch(
      getAgentReferrals({
        id: id,
        page: currentPage,
        search: searchValue,
        status: status,
      })
    );
  }, [dispatch, status, id, currentPage, searchValue]);

  // const navigate = useNavigate()
  return (
    <div>
      <TableBackground>
        {/* SEARCH AND FILTER SECTION */}
        <section className="flex gap-4  pt-4">
          <div className="w-full relative px-4">
            <SearchComponent
              placeholder="Search..."
              searchValue={searchValue}
              handleChange={handleChange}
            />
          </div>
        </section>

        {/* TABS SECTION */}
        <section>
          <div className="flex gap-4 mt-4 mb-5 cursor-pointer px-4">
            <div
              className={
                status === "all"
                  ? "flex text-primary border-b-primary border-b-[2px] pb-[4px]"
                  : "flex text-[#96A397] pb-[4px]"
              }
              onClick={() => handleStatusChange("all")}
            >
              <div className="flex">
                <p className=" leading-[18px] text-sm font-semibold pr-[4px]">
                  All
                </p>
                <div
                  className={
                    status === "all"
                      ? "bg-[#DBFFDF] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                      : "bg-[#ECEEEC] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                  }
                >
                  <p>{status === "all" ? agentReferralTotal : "0"}</p>
                </div>
              </div>
            </div>

            <div
              className={
                status === "complete"
                  ? "flex text-primary border-b-primary border-b-[2px] pb-[4px]"
                  : "flex text-[#96A397] pb-[4px]"
              }
              onClick={() => handleStatusChange("complete")}
            >
              <div className="flex">
                <p className=" leading-[18px] text-sm font-semibold pr-[4px]">
                  Complete
                </p>
                <div
                  className={
                    status === "complete"
                      ? "bg-[#DBFFDF] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                      : "bg-[#ECEEEC] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                  }
                >
                  <p>{status === "complete" ? agentReferralTotal : "0"}</p>
                </div>
              </div>
            </div>

            <div
              className={
                status === "incomplete"
                  ? "flex text-primary border-b-primary border-b-[2px] pb-[4px]"
                  : "flex text-[#96A397] pb-[4px]"
              }
              onClick={() => handleStatusChange("incomplete")}
            >
              <div className="flex">
                <p className=" leading-[18px] text-sm font-semibold pr-[4px]">
                  Incomplete
                </p>
                <div
                  className={
                    status === "incomplete"
                      ? "bg-[#DBFFDF] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                      : "bg-[#ECEEEC] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                  }
                >
                  <p>{status === "incomplete" ? agentReferralTotal : "0"}</p>
                </div>
              </div>
            </div>

            <div
              className={
                status === "temporary"
                  ? "flex text-primary border-b-primary border-b-[2px] pb-[4px]"
                  : "flex text-[#96A397] pb-[4px]"
              }
              onClick={() => handleStatusChange("temporary")}
            >
              <div className="flex">
                <p className=" leading-[18px] text-sm font-semibold pr-[4px]">
                  Temporary
                </p>
                <div
                  className={
                    status === "temporary"
                      ? "bg-[#DBFFDF] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                      : "bg-[#ECEEEC] rounded-[4px] py-[2px] px-[4px]  leading-[14px] text-[10px] font-semibold "
                  }
                >
                  <p>{status === "temporary" ? agentReferralTotal : "0"}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pt-[5px]">
          {agentReferralLoading ? (
            <div className="animate-pulse w-full mt-2 mb-4 pb-4 px-4">
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full"></div>
            </div>
          ) : (
            <>
              {agentReferralData.length === 0 ? (
                <div>
                  <EmptyState text="There's no data for this filter yet" />
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="px-4 pb-2">
                        First Name
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Last Name
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Phone Number
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Cooperative
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        State
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        LGA
                      </th>
                      <th scope="col" className="px-4 pb-2">
                        Registration Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentReferralData.map((data, index) => {
                      return (
                        <tr
                          key={index}
                          className="relative cursor-pointer hover:bg-[#edf7ee]"
                          onClick={() =>
                            navigate(`/farmers-profile/${data?.farmer_id}`)
                          }
                        >
                          <td className="px-4">
                            {data?.farmer?.first_name || "N/A"}
                          </td>
                          <td className="px-4">
                            {data?.farmer?.last_name || "N/A"}
                          </td>
                          <td className="px-4">
                            {data?.farmer?.phone_number || "N/A"}
                          </td>
                          <td className="px-4">
                            {data?.farmer?.cooperative || "N/A"}
                          </td>
                          <td className="px-4">
                            {data?.farmer?.state || "N/A"}
                          </td>
                          <td className="px-4">{data?.farmer?.lga || "N/A"}</td>
                          <td className="px-4">
                            <div
                              className={
                                data?.farmer?.registration_status === "Complete"
                                  ? "px-4 success-badge"
                                  : data?.farmer?.registration_status ===
                                    "Incomplete"
                                  ? "ml-4 px-4 error-badge"
                                  : "ml-4 px-4 na-badge"
                              }
                            >
                              <p className="capitalize">
                                {data?.farmer?.registration_status || "N/A"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}

          {!agentReferralLoading && agentReferralData.length > 0 ? (
            <div className="mt-1 pl-[-16px]" style={{ paddingLeft: "-16px" }}>
              <PaginationComponent
                page={currentPage}
                onPageChange={(page) => onPageChange({ page })}
                pageSize={10}
                totalItems={agentReferralTotal}
              />
            </div>
          ) : (
            ""
          )}
        </section>
      </TableBackground>
    </div>
  );
};

export default ReferralsTable;
